import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import _ from 'lodash';
import {
  Input,
  UncontrolledTooltip,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  formatBytes,
  getFileExtension,
  getImageResolution,
  getFileNameFromPath,
  getFileNameWithoutExtension,
} from '../../helpers/Files';

import UploadFileThumbnail from './FileThumbnail';
import UploadFileProgress from './FileProgress';

class UploadFile extends Component {
  constructor(props) {
    super(props);

    this.state = {
      error: false,
    };

    this.onStatusChange = this.onStatusChange.bind(this);
    this.handleRetry = this.handleRetry.bind(this);
    this.handleRemove = this.handleRemove.bind(this);
  }

  componentDidMount() {
    const { uploader } = this.props;
    uploader.on('statusChange', this.onStatusChange);
  }

  componentDidUpdate() {
    const { error } = this.state;
    const { file } = this.props;

    if (error && file.uploadStatus === 'uploading') {
      this.setState({ error: false });
    }
  }

  componentWillUnmount() {
    this.unregisterEventHandlers();
  }

  onStatusChange(fileId, oldStatus, newStatus) {
    const {
      uploader,
      id,
    } = this.props;
    const statusEnum = uploader.qq.status;

    if (newStatus === statusEnum.UPLOAD_FAILED && fileId === id) {
      this.setState({
        error: true,
      });
    }
  }

  unregisterEventHandlers() {
    const { uploader } = this.props;
    uploader.off('statusChange', this.onStatusChange);
  }

  handleRetry(e) {
    const {
      id,
      handleRetry,
    } = this.props;

    e.preventDefault();
    this.setState({ error: false });
    handleRetry(id);
  }

  handleRemove(e) {
    const {
      id,
      handleRemove,
    } = this.props;

    e.preventDefault();
    handleRemove(id);
  }

  render() {
    const {
      intl,
      id,
      uploader,
      file,
      collections,
      mode,
      toggleSelected,
      isMapping,
      isLinkEdit,
      uploadedFilesFilter,
      includePrefix,
      prefix,
      includeSuffix,
      suffix,
    } = this.props;
    const { error } = this.state;
    const resolution = getImageResolution(file);
    const isCompleted = file.uploadStatus === 'completed';
    const isFailed = file.uploadStatus === 'failed';
    const isInvalid = file.uploadStatus === 'invalid';
    const isSelected = file.selected;
    const isValidationError = (
      !_.isEmpty(file.errors?.taxonomy) ||
      !_.isEmpty(file.errors?.customFields) ||
      !_.isEmpty(file.errors?.customFieldsUrls) ||
      file.errors?.expirationDate ||
      file.errors?.status
    ) && file.uploadStatus === 'completed';
    const extension = getFileExtension(file);
    let filename = file.name ? file.name : getFileNameFromPath(uploader.methods.getName(id));
    if (!filename && file.original_name) {
      filename = file.original_name;
    }
    if (!filename && mode === 'link') {
      filename = file.path;
    }
    if (includeSuffix) {
      filename = getFileNameWithoutExtension(filename, extension);
    }
    const size = file.size ? file.size : uploader.methods.getSize(id);
    const collection = collections.length === 1 ? collections[0] : false;

    if (uploadedFilesFilter === 'failed' && !(isFailed || error || isInvalid || isValidationError)) {
      return null;
    }

    return (
      <li className={classnames('uploaded-file', { error: isFailed || error || isInvalid || isValidationError, 'file-validation-error': isValidationError })}>
        {(isFailed || error) ? (
          <a href="#retry" className="retry-button" title="Retry" onClick={this.handleRetry}>
            <i className="fas fa-redo" />
          </a>
        ) : (
          <Input
            type="checkbox"
            className={`input-check check-l${isMapping ? ' hidden' : ''}`}
            id={`uploaded-file-${id}`}
            checked={isSelected}
            onChange={() => { toggleSelected(id); }}
          />
        )}
        <label className="uploaded-file-wrapper" htmlFor={`uploaded-file-${id}`}>
          <UploadFileThumbnail
            completed={isCompleted}
            error={error || isFailed}
            file={file}
          />
          {(mode !== 'edit' && !isLinkEdit) && (
            <div className="uploaded-file-remove">
              <a href="#remove" className="remove-button" title="Remove" onClick={this.handleRemove}>
                <i className="fas fa-times-circle" />
              </a>
            </div>
          )}
          <div className="uploaded-file-body">
            {file.folder && (
              <span className="uploaded-file-folder file-details">
                {file.folder}
              </span>
            )}
            <h6 className="uploaded-file-title">
              {file.type === 'link' && (
                <>
                  <i className="fa-solid fa-link-horizontal" />
                  {' '}
                </>
              )}
              {mode === 'edit' ? (
                <>
                  {prefix}
                  {filename}
                  {suffix}
                </>
              ) : (
                <>
                  {includePrefix && prefix}
                  {filename}
                  {includeSuffix && suffix}
                </>
              )}
            </h6>
            {(mode !== 'link' && !(isCompleted || isFailed || error)) && (
              <UploadFileProgress
                id={id}
                uploader={uploader}
              />
            )}
            {(isFailed || error) && (
              <span className="error">
                {intl.formatMessage({ defaultMessage: 'Upload failed. Please try again.' })}
              </span>
            )}
            {isValidationError && (
              <span className="error file-validation-error">
                {mode === 'edit' ? (
                  intl.formatMessage({ defaultMessage: 'Metadata is required to save.' })
                ) : (
                  intl.formatMessage({ defaultMessage: 'Metadata is required to upload.' })
                )}
              </span>
            )}
            {isInvalid && (
              <span className="error">
                {intl.formatMessage({ defaultMessage: 'URL of this link is invalid' })}
              </span>
            )}
            {(mode !== 'link' && isCompleted && !(error || isFailed || isValidationError)) && (
              <div className="uploaded-file-details file-details">
                <div>{collections.length <= 1 && (
                  <span className="file-access">
                    {(collection !== false && collection.spreadPermissions) ? (
                      <>
                        <i className={`fas fa-${collection.accessIcon}`} id={`file-access-${file.uuid}`} />
                        <UncontrolledTooltip fade={false} target={`file-access-${file.uuid}`}>
                          {collection.accessDescription}
                        </UncontrolledTooltip>
                      </>
                    ) : (
                      <i className={`fas fa-${file.public ? 'eye' : 'lock'}`} />
                    )}
                  </span>
                )}
                  {resolution && (
                    <span className="file-resolution">
                      {resolution}
                    </span>
                  )}
                  {extension && (
                    <span className="file-extension">
                      {extension}
                    </span>
                  )}
                  <span className="file-size">
                    {formatBytes(size)}
                  </span>
                </div>
              </div>
            )}
            {(mode === 'link' && file.website) && (
              <div className="uploaded-file-details file-details">
                <div>
                  <span className="file-link-website">
                    {file.website.name}
                  </span>
                </div>
              </div>
            )}
          </div>
        </label>
      </li>
    );
  }
}

UploadFile.propTypes = {
  intl: PropTypes.object.isRequired,
  id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  uploader: PropTypes.object.isRequired,
  file: PropTypes.object.isRequired,
  collections: PropTypes.array,
  mode: PropTypes.string.isRequired,
  isMapping: PropTypes.bool.isRequired,
  isLinkEdit: PropTypes.bool.isRequired,
  toggleSelected: PropTypes.func.isRequired,
  handleRetry: PropTypes.func.isRequired,
  handleRemove: PropTypes.func.isRequired,
  uploadedFilesFilter: PropTypes.string.isRequired,
  includePrefix: PropTypes.bool.isRequired,
  prefix: PropTypes.string.isRequired,
  includeSuffix: PropTypes.bool.isRequired,
  suffix: PropTypes.string.isRequired,
};

UploadFile.defaultProps = {
  collections: [],
};

export default injectIntl(UploadFile);
